import { useCallback, useEffect, useMemo, useState } from "react"
import type { RefObject } from "react"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"
import { Box, useTheme } from "@mui/material"

import type { UsersLog } from "types/users.types"
import type { DateRange } from "types/date.types"

import { useOrgUsersLogsQuery } from "features/api"
import { selectCurrentOrgId, selectCurrentOrgName } from "features/store/orgSlice"
import useDateRange from "helpers/hooks/useDateRange"
import SkeletonWrapper from "widgets/common/SkeletonWrapper"
import DataGridTable from "widgets/common/DataGridTable"
import DateRangePicker from "widgets/dates/DateRangePicker"
import AuditTrailCsvModal from "./AuditTrailCsvModal"
import {
  getAuditTrailCsvFilename,
  getAuditTrailTableColumns,
  hasMoreResults,
  updateTableData,
} from "./helpers"

const DEFAULT_PAGE_SIZE = 10

interface AuditTrailTableProps {
  scrollAnchorRef?: RefObject<HTMLElement | null>
}

const AuditTrailTable = ({ scrollAnchorRef }: AuditTrailTableProps) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const orgId = useSelector(selectCurrentOrgId)
  const orgName = useSelector(selectCurrentOrgName)
  const { getDateRangeTools } = useDateRange()
  const {
    dateRange: auditTrailDateRange,
    dispatchDateRange: dispatchAuditTrailDateRange,
    isDateRangeValid: isAuditTrailDateRangeValid,
  } = getDateRangeTools("auditTrail")
  const [results, setResults] = useState<UsersLog[]>([])
  const [cursor, setCursor] = useState<string | undefined>()
  // Each request fetches one page.
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
  const [showSkeleton, setShowSkeleton] = useState(true)
  const [isCsvModalOpen, setIsCsvModalOpen] = useState(false)
  const { data, currentData, isFetching, isError, refetch } = useOrgUsersLogsQuery(
    {
      org_id: Number(orgId),
      from_date: String(auditTrailDateRange?.fromDate),
      to_date: String(auditTrailDateRange?.toDate),
      cursor,
      limit: pageSize,
    },
    {
      skip:
        !auditTrailDateRange?.fromDate ||
        !auditTrailDateRange?.toDate ||
        !isAuditTrailDateRangeValid ||
        !orgId,
    },
  )

  // `data` outlives a failed request: back on a cached range it would be the same object, and
  // the rows would never come back.
  useEffect(() => {
    updateTableData(currentData, results, setResults, setShowSkeleton)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentData])

  const columns = useMemo(() => getAuditTrailTableColumns(t), [t])
  const csvFileName = useMemo(
    () => getAuditTrailCsvFilename(orgName, auditTrailDateRange, t),
    [orgName, auditTrailDateRange, t],
  )

  const openCsvModal = useCallback(() => setIsCsvModalOpen(true), [])
  const closeCsvModal = useCallback(() => setIsCsvModalOpen(false), [])

  const resetTable = useCallback(() => {
    setShowSkeleton(true)
    setResults([])
    setCursor(undefined)
  }, [])

  // The pages loaded so far are of the old size, so it starts over.
  const handlePageSizeChange = useCallback(
    (newPageSize: number) => {
      resetTable()
      setPageSize(newPageSize)
    },
    [resetTable],
  )

  const dispatchDateRange = useCallback(
    (dateRange: DateRange) => {
      // Same dates, same cached data: the effect that hides the skeleton would never run
      const hasSameDates =
        dateRange.fromDate === auditTrailDateRange?.fromDate &&
        dateRange.toDate === auditTrailDateRange?.toDate
      if (!hasSameDates) {
        resetTable()
      }
      // Always dispatched: the URL is already updated, and `range` can change alone
      dispatchAuditTrailDateRange(dateRange)
    },
    [
      auditTrailDateRange?.fromDate,
      auditTrailDateRange?.toDate,
      dispatchAuditTrailDateRange,
      resetTable,
    ],
  )

  useEffect(() => {
    // This table date range is not refreshing with an interval
    // Need to reset audit trail date range to null on unmount
    // This is for the date range to be recalculated on mount
    return () => {
      dispatchAuditTrailDateRange(null)
    }
  }, [dispatchAuditTrailDateRange])

  return (
    <>
      <Box
        sx={{
          p: "0 0 0.75rem 0",
          width: "100%",
          display: "flex",
          justifyContent: "flex-end",
        }}
      >
        <DateRangePicker
          dateRange={auditTrailDateRange}
          dispatchDateRange={dispatchDateRange}
          isFetching={isFetching}
        />
      </Box>
      {/* A failed request brings no data, and only data hides the skeleton */}
      <SkeletonWrapper isWaiting={showSkeleton && !isError} height={540} width={"100vw"}>
        <DataGridTable
          rows={results}
          columns={columns}
          pageSize={pageSize}
          onPageSizeChange={handlePageSizeChange}
          onExportCsv={openCsvModal}
          // The quick filter only searches the pages loaded so far.
          disableSearch
          cursorPagination={{
            // Without rows the table was just reset, and `data` may still be another range's.
            hasMore: results.length > 0 && hasMoreResults(data, pageSize),
            loading: isFetching,
            // A failed page leaves `data` as the previous one, whose cursor is the one that failed:
            // setting it again would change nothing, so it is retried instead.
            onNextPage: () => (data?.next === cursor ? refetch() : setCursor(data?.next)),
          }}
          emptyContent={
            isError && !results.length ? t("error.COULD_NOT_LOAD_DATA") : undefined
          }
          sx={{ border: `1px solid ${theme.palette.neutral[400]}` }}
          scrollAnchorRef={scrollAnchorRef}
        />
      </SkeletonWrapper>
      <AuditTrailCsvModal
        open={isCsvModalOpen}
        onClose={closeCsvModal}
        columns={columns}
        range={auditTrailDateRange?.range}
        fromDate={auditTrailDateRange?.fromDate}
        toDate={auditTrailDateRange?.toDate}
        fileName={csvFileName}
      />
    </>
  )
}

export default AuditTrailTable
